import type { FC, KeyboardEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { ArrowUpRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import MockupFigure from '@/components/landing/mockup-figure'
import PhoneFrame from '@/components/landing/mockups/phone-frame'
import SectionIntro, { Eyebrow } from '@/components/landing/section-intro'
import { CreationScreen, MirrorScreen, SportsScreen } from '@/components/landing/mockups/use-case-screens'

interface UseCase {
    id: string
    number: string
    name: string
    title: [string, string]
    description: string
    points: string[]
    link: { label: string, href: string }
    caption: string
    figure: string
    screen: ReactNode
}

const USE_CASES: UseCase[] = [
    {
        id: 'sports',
        number: '01',
        name: 'Sports',
        title: ['Review every phase', 'of the movement.'],
        description: "Place two takes, or two moments of one clip, side by side. Slow it down and zoom into the area you want to review.",
        points: ['Independent zoom per view', 'Speed and volume per video', 'Overlay two takes'],
        link: { label: 'See the tools', href: '#features' },
        caption: 'Motion comparison · Illustrative view',
        figure: 'Illustrative view: two moments of the same jump side by side, one slowed down and one zoomed in',
        screen: <SportsScreen />
    },
    {
        id: 'dance',
        number: '02',
        name: 'Dance',
        title: ['Repeat the step,', 'at your own pace.'],
        description: "Compare a clip with its mirrored version or place your rehearsal next to a reference. Adjust the speed to follow every step.",
        points: ['Mirror mode per video', 'References and rehearsals in view', 'Playlists to organize your clips'],
        link: { label: 'See the tools', href: '#features' },
        caption: 'Mirror mode · Illustrative view',
        figure: 'Illustrative view: a dancer next to the mirrored version of the same clip',
        screen: <MirrorScreen />
    },
    {
        id: 'creation',
        number: '03',
        name: 'Creation',
        title: ['Many clips.', 'One MP4.'],
        description: "Mix videos and photos, pick a grid or a Manga layout and export a local scene to share.",
        points: ['Classic grids and Manga layouts', 'Frames, filters and backgrounds', 'Local scenes in MP4 format'],
        link: { label: 'See export', href: '#export' },
        caption: 'Manga composition · Illustrative view',
        figure: 'Illustrative view: four clips arranged in Manga panels, ready to export',
        screen: <CreationScreen />
    }
]

const UseCasesSection: FC = (): ReactNode => {
    const [active, setActive] = useState(0)
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        const count = USE_CASES.length
        let next = index

        if (event.key === 'ArrowRight') next = (index + 1) % count
        else if (event.key === 'ArrowLeft') next = (index - 1 + count) % count
        else if (event.key === 'Home') next = 0
        else if (event.key === 'End') next = count - 1
        else return

        event.preventDefault()
        setActive(next)
        tabRefs.current[next]?.focus()
    }

    return (
        <section id='use-cases' aria-labelledby='use-cases-title' className='border-t border-border bg-card py-20 lg:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionIntro
                    id='use-cases-title'
                    eyebrow='Pick your use'
                    title={(
                        <>
                            Compare a technique.
                            <br />
                            Prepare your next video.
                        </>
                    )}
                    intro='A reference and your rehearsal. Two angles of one play. Several clips for a composition. All in view.'
                />

                <div
                    role='tablist'
                    aria-label='Use cases'
                    className='no-scrollbar -mx-5 mt-10 flex gap-2.5 overflow-x-auto px-5 pb-1 sm:mx-0 sm:px-0'
                >
                    {USE_CASES.map((useCase, index) => {
                        const selected = index === active

                        return (
                            <button
                                key={useCase.id}
                                ref={(element) => {
                                    tabRefs.current[index] = element
                                }}
                                type='button'
                                role='tab'
                                id={`use-case-tab-${useCase.id}`}
                                aria-selected={selected}
                                aria-controls={`use-case-panel-${useCase.id}`}
                                tabIndex={selected ? 0 : -1}
                                onClick={() => setActive(index)}
                                onKeyDown={(event) => handleKeyDown(event, index)}
                                className={cn(
                                    'relative inline-flex shrink-0 items-center gap-2.5 overflow-hidden rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors',
                                    selected
                                        ? 'border-primary bg-accent text-foreground'
                                        : 'border-border bg-muted/60 text-foreground/75 hover:bg-muted hover:text-foreground'
                                )}
                            >
                                <span className={cn('font-mono text-[0.7rem]', selected ? 'text-brand' : 'text-muted-foreground')}>
                                    {useCase.number}
                                </span>

                                {useCase.name}

                                {selected && (
                                    <span aria-hidden='true' className='absolute inset-x-3 bottom-0 h-[3px] rounded-t-full bg-primary' />
                                )}
                            </button>
                        )
                    })}
                </div>

                {USE_CASES.map((useCase, index) => (
                    <div
                        key={useCase.id}
                        role='tabpanel'
                        id={`use-case-panel-${useCase.id}`}
                        aria-labelledby={`use-case-tab-${useCase.id}`}
                        hidden={index !== active}
                        className='mt-6'
                    >
                        <div className='grid items-center gap-10 animate-in fade-in slide-in-from-bottom-2 duration-500 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-16'>
                            <MockupFigure
                                label={useCase.figure}
                                title={useCase.caption.replace(' · Illustrative view', '')}
                                caption={useCase.caption}
                                className='rounded-2xl border border-border bg-card'
                            >
                                <PhoneFrame>
                                    {useCase.screen}
                                </PhoneFrame>
                            </MockupFigure>

                            <div>
                                <Eyebrow>
                                    {useCase.name}
                                </Eyebrow>

                                <h3 className='mt-4 font-heading text-[clamp(1.9rem,3.6vw,2.6rem)] font-extrabold leading-[1.05] tracking-[-0.03em] text-foreground'>
                                    {useCase.title[0]}
                                    <br />
                                    {useCase.title[1]}
                                </h3>

                                <p className='mt-5 max-w-md text-base leading-relaxed text-muted-foreground'>
                                    {useCase.description}
                                </p>

                                <ul className='mt-7 space-y-3.5'>
                                    {useCase.points.map((point) => (
                                        <li key={point} className='flex items-center gap-3 text-[0.95rem] text-foreground/90'>
                                            <Check className='size-4 shrink-0 text-brand' strokeWidth={2.5} />
                                            {point}
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href={useCase.link.href}
                                    className='group mt-8 inline-flex items-center gap-2 text-sm font-bold text-brand'
                                >
                                    {useCase.link.label}
                                    <ArrowUpRight className='size-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                </a>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    )
}

export default UseCasesSection
